import { buildPhraseChords, type PhraseChord } from "@/components/learn-piece/phrase-chords";
import type { Phrase, SongSpan } from "@/lib/learning-journey/types";
import type { AnalysisChord } from "@/lib/music/analyzer-types";

/**
 * Ce qu'un niveau met vraiment sous les mains, fenêtre d'accord par fenêtre.
 *
 * ## Pourquoi une fenêtre vide n'est pas un pas
 *
 * L'analyse découpe l'harmonie du **morceau** ; un niveau, lui, n'en garde
 * qu'une partie des notes. Les deux découpages ne coïncident donc pas, et une
 * fenêtre peut très bien ne recevoir aucune note du niveau : un passage que ce
 * niveau ne dote pas encore.
 *
 * `chordHeld` demande que toutes les touches attendues soient enfoncées et
 * qu'il y en ait au moins une, donc une fenêtre vide n'est jamais prise. La
 * laisser dans la suite fabrique une impasse muette : l'écran nomme un accord,
 * en donne le degré et le rôle, n'affiche aucune note à jouer, et rien ne peut
 * plus avancer. C'est exactement ce que produisait l'unité 2 du niveau 1 du
 * *Lac des cygnes*, sur un `Bm/D` rogné.
 *
 * La règle est donc en amont, et elle est la même pour la suite d'accords et
 * pour le fil qui la montre : **ce que ce niveau ne fait pas jouer n'est pas
 * montré**. Elle ne cache rien de l'harmonie du morceau, qui reste entière sur
 * la carte et sur le roll ; elle dit seulement de quoi cet écran-ci parle.
 *
 * Ce n'est pas `buildPhraseChords` qui filtre : le parcours par cœur lit les
 * mêmes fenêtres pour décrire un passage, où une fenêtre sans note de la main
 * travaillée reste une information.
 *
 * ## Taillées sur le morceau entier, pas sur les unités
 *
 * Les fenêtres se taillaient unité par unité, et une unité ne porte que les
 * notes qui **commencent** chez elle. Un accord tenu par-dessus une frontière
 * d'unité sortait donc en deux fenêtres, dont la seconde ne recevait aucune
 * note : vide, elle était retirée, et la frise dessinait l'accord jusqu'à la
 * frontière puis un trou jusqu'à l'accord suivant. Deux temps de rien sous le
 * ré mineur de *The Sound of Silence*, dont la première phrase s'arrête au
 * milieu de lui. Le `Bm/D` du *Lac des cygnes* était ce même accord coupé :
 * avant le filtre, sa moitié vide bloquait l'écran ; avec lui, elle laissait
 * ce trou.
 *
 * Le niveau se traverse d'un trait, et les unités ne sont plus que le grain de
 * la mesure : les fenêtres se taillent donc sur l'étendue du morceau, où un
 * accord n'est rogné par rien. Voir `buildSongSpan`.
 */
export function playableChords(
  chords: readonly AnalysisChord[],
  span: Pick<SongSpan, "startTick" | "endTick" | "notesByHand">,
): PhraseChord[] {
  return buildPhraseChords(chords, span).filter((entry) => entry.midis.length > 0);
}

/**
 * Les unités dont ce niveau demande quelque chose.
 *
 * Une unité dont toutes les fenêtres sont vides n'est pas une unité à finir,
 * c'est une unité que ce niveau ne touche pas. La faire monter afficherait un
 * écran sans rien à jouer ; la compter parmi les unités du niveau ferait
 * attendre une fin qui ne peut pas venir, puisque la traversée refuse, à juste
 * titre, de déclarer finie une suite de zéro accord.
 *
 * C'est la même règle que `carriedOver` côté échelle, prise à l'autre bout : là
 * on saute ce qu'un niveau ne **change** pas, ici ce qu'il ne **donne** pas.
 *
 * Unité par unité, elle, parce que c'est son objet : ce sont les unités que la
 * session compte.
 */
export function unitsWithSomethingToPlay(
  chords: readonly AnalysisChord[],
  units: readonly Phrase[],
): Phrase[] {
  return units.filter((phrase) => playableChords(chords, phrase).length > 0);
}
